import { useEffect, useState, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  fetchControlledSubstances,
  fetchControlledMedications,
} from '../store/controlledSubstancesSlice'
import { fetchVeterinarians } from '../store/settingsSlice'
import { ControlledSubstancesTable } from '../components/controlled-substances/ControlledSubstancesTable'
import { AddEntryModal } from '../components/controlled-substances/AddEntryModal'
import { Plus, Download, ChevronDown } from 'lucide-react'

const transactionTypeOptions = ['Received', 'Dispensed', 'Wasted', 'Adjustment'] as const

function formatRxNumber(num: number | null): string {
  if (num == null) return ''
  return `RX-${String(num).padStart(4, '0')}`
}

function formatDateTimeCSV(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export function ControlledSubstancesLogPage() {
  const dispatch = useAppDispatch()
  const { entries, medications, loading } = useAppSelector((s) => s.controlledSubstances)

  const [modalOpen, setModalOpen] = useState(false)
  const [dateStart, setDateStart] = useState('')
  const [dateEnd, setDateEnd] = useState('')
  const [medicationFilter, setMedicationFilter] = useState('')
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('')

  // Dropdowns open state
  const [medDropdownOpen, setMedDropdownOpen] = useState(false)
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchControlledSubstances())
    dispatch(fetchControlledMedications())
    dispatch(fetchVeterinarians())
  }, [dispatch])

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      if (dateStart) {
        const entryDate = new Date(entry.transaction_date).toISOString().slice(0, 10)
        if (entryDate < dateStart) return false
      }
      if (dateEnd) {
        const entryDate = new Date(entry.transaction_date).toISOString().slice(0, 10)
        if (entryDate > dateEnd) return false
      }
      if (medicationFilter && entry.medication_id !== medicationFilter) return false
      if (transactionTypeFilter && entry.transaction_type !== transactionTypeFilter) return false
      return true
    })
  }, [entries, dateStart, dateEnd, medicationFilter, transactionTypeFilter])

  const handleExportCSV = () => {
    const headers = [
      'Date/Time',
      'Medication Name',
      'DEA Schedule',
      'Transaction Type',
      'Quantity',
      'Patient Name',
      'Rx Number',
      'Performed By',
      'Witnessed By',
      'Running Balance',
    ]

    const rows = filteredEntries.map((entry) => [
      formatDateTimeCSV(entry.transaction_date),
      entry.medication_name,
      entry.dea_schedule ? `Schedule ${entry.dea_schedule}` : '',
      entry.transaction_type,
      String(entry.quantity),
      entry.patient_name || '',
      entry.prescription_number ? formatRxNumber(entry.prescription_number) : '',
      entry.performed_by,
      entry.witnessed_by || '',
      String(entry.running_balance),
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const dateStamp = new Date().toISOString().slice(0, 10)
    link.href = url
    link.download = `controlled-substances-log-${dateStamp}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const selectedMedName = medicationFilter
    ? medications.find((m) => m.id === medicationFilter)?.name || 'All'
    : 'All'

  if (loading) {
    return (
      <div className="p-6 max-sm:p-3" data-testid="controlled-substances-page">
        <p className="text-[13px] text-text-muted">Loading controlled substances log...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-sm:p-3" data-testid="controlled-substances-page">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-[16px] font-semibold text-text-primary">Controlled Substances Log</h1>
        <div className="flex items-center gap-2">
          <button
            data-testid="export-csv-button"
            onClick={handleExportCSV}
            disabled={filteredEntries.length === 0}
            className="inline-flex items-center gap-1.5 h-7 px-2 rounded-[4px] text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer disabled:opacity-50 disabled:cursor-default"
          >
            <Download size={14} />
            <span className="max-sm:hidden">Export CSV</span>
          </button>
          <button
            data-testid="add-entry-button"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1.5 h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
          >
            <Plus size={14} />
            <span className="max-sm:hidden">Add Entry</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div data-testid="controlled-substances-filters" className="flex items-center gap-3 mb-4 flex-wrap">
        {/* Date Range */}
        <div className="flex items-center gap-1.5">
          <label className="text-[11px] text-text-muted">From</label>
          <input
            data-testid="filter-date-start"
            type="date"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            className="h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[11px] text-text-muted">To</label>
          <input
            data-testid="filter-date-end"
            type="date"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
            className="h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>

        {/* Medication Filter */}
        <div className="relative">
          <button
            data-testid="filter-medication"
            onClick={() => { setMedDropdownOpen(!medDropdownOpen); setTypeDropdownOpen(false) }}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            <span className="text-[11px] text-text-muted mr-0.5">Medication:</span>
            {selectedMedName}
            <ChevronDown size={12} className="text-text-muted" />
          </button>
          {medDropdownOpen && (
            <div
              data-testid="filter-medication-dropdown"
              className="absolute z-10 top-full left-0 mt-1 bg-surface border border-border rounded-[4px] min-w-[180px] max-h-40 overflow-y-auto"
              style={{ boxShadow: 'var(--shadow-elevation-1)' }}
            >
              <button
                type="button"
                data-testid="filter-medication-option-all"
                onClick={() => { setMedicationFilter(''); setMedDropdownOpen(false) }}
                className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
              >
                All
              </button>
              {medications.map((med) => (
                <button
                  key={med.id}
                  type="button"
                  data-testid={`filter-medication-option-${med.id}`}
                  onClick={() => { setMedicationFilter(med.id); setMedDropdownOpen(false) }}
                  className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                >
                  {med.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Transaction Type Filter */}
        <div className="relative">
          <button
            data-testid="filter-transaction-type"
            onClick={() => { setTypeDropdownOpen(!typeDropdownOpen); setMedDropdownOpen(false) }}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            <span className="text-[11px] text-text-muted mr-0.5">Type:</span>
            {transactionTypeFilter || 'All'}
            <ChevronDown size={12} className="text-text-muted" />
          </button>
          {typeDropdownOpen && (
            <div
              data-testid="filter-transaction-type-dropdown"
              className="absolute z-10 top-full left-0 mt-1 bg-surface border border-border rounded-[4px] min-w-[160px] max-h-40 overflow-y-auto"
              style={{ boxShadow: 'var(--shadow-elevation-1)' }}
            >
              <button
                type="button"
                data-testid="filter-type-option-all"
                onClick={() => { setTransactionTypeFilter(''); setTypeDropdownOpen(false) }}
                className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
              >
                All
              </button>
              {transactionTypeOptions.map((type) => (
                <button
                  key={type}
                  type="button"
                  data-testid={`filter-type-option-${type}`}
                  onClick={() => { setTransactionTypeFilter(type); setTypeDropdownOpen(false) }}
                  className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                >
                  {type}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <ControlledSubstancesTable entries={filteredEntries} />
      </div>

      {/* Add Entry Modal */}
      <AddEntryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        medications={medications}
      />
    </div>
  )
}
